import { FileExportResponse } from '../../models/FIleModels';
import { OrderSetRequest, OrderSetResponse, UpdateOrderSetRequest } from '../../models/OrderSetModels';
import {
    CreateLocationRequest,
    CreateNewOrganizationRequest,
    HospiceOrganizationWithDetailsResponse,
    ImportHospiceOrganizationResponse,
    PlaceOfServiceRequest,
    UpsertTeamRequest,
} from '../../models/OrganizationModels';
import { MedOrderRequest, QueueMedOrderRequest } from '../../models/PatientModels';
import { RpaAvailability } from '../../models/RpaModels';
import {
    ScriptSureUserWithDetailsDto,
    ToggleUserStatusRequest,
    UserWithDetailsDto,
} from '../../models/UserResponses';
import { baseApiService } from '../baseApiService';

export const organizationApi = baseApiService
    .enhanceEndpoints({
        addTagTypes: ['organizationList'],
    })
    .injectEndpoints({
        endpoints: (builder) => ({
            getOrganizations: builder.query<
                HospiceOrganizationWithDetailsResponse[],
                {}
            >({
                query: () => ({
                    url: `/v1/hospiceorganization/full`,
                    method: 'GET',
                }),
                providesTags: ['organizationList'],
            }),

            exportOrganizationFile: builder.mutation<
                FileExportResponse,
                { organizationId: string; fileName: string }
            >({
                query: ({ organizationId, fileName }) => ({
                    url: `/v1/hospiceorganization/${organizationId}/file-export`,
                    method: 'POST',
                    body: { fileName },
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }),
            }),

            getOrganizationUsers: builder.query<UserWithDetailsDto[], string>({
                query: (id) => ({
                    url: `/users/details?organizationId=${id}`,
                    method: 'GET',
                }),
            }),

            getUser: builder.query<UserWithDetailsDto, string>({
                query: (id) => ({
                    url: `/users/${id}/details`,
                    method: 'GET',
                }),
            }),

            getScriptSureUserQ: builder.query<
                ScriptSureUserWithDetailsDto,
                string
            >({
                query: (id) => ({
                    url: `/users/scriptsure/${id}`,
                    method: 'GET',
                }),
            }),

            getScriptSureUser: builder.mutation<
                ScriptSureUserWithDetailsDto,
                string
            >({
                query: (id) => ({
                    url: `/users/scriptsure/${id}`,
                    method: 'GET',
                }),
            }),

            createUserFromScriptSureUser: builder.mutation<
                ImportHospiceOrganizationResponse,
                ScriptSureUserWithDetailsDto
            >({
                query: (data: ScriptSureUserWithDetailsDto) => ({
                    url: `/users/from-scriptsure`,
                    method: 'POST',
                    body: data,
                }),
            }),

            toggleUserStatus: builder.mutation<
                boolean,
                ToggleUserStatusRequest
            >({
                query: (data: ToggleUserStatusRequest) => ({
                    url: `/users/${data.userId}/toggle-status`,
                    method: 'POST',
                    body: data,
                }),
            }),

            createTeamByLocationId: builder.mutation<any, UpsertTeamRequest>(
                {
                    query: (data: UpsertTeamRequest) => {
                        return {
                            url: `/v1/team`,
                            method: 'POST',
                            body: data,
                            headers: {
                                'Content-Type': 'application/json',
                            },
                        };
                    },
                    invalidatesTags: ['organizationList'],
                },
            ),

            createLocation: builder.mutation<any, CreateLocationRequest>({
                query: (data: CreateLocationRequest) => ({
                    url: `/v1/location`,
                    method: 'POST',
                    body: data,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }),
                invalidatesTags: ['organizationList'],
            }),

            createPlaceOfService: builder.mutation<any, PlaceOfServiceRequest>({
                query: (data: PlaceOfServiceRequest) => ({
                    url: `/v1/placeofservices`,
                    method: 'POST',
                    body: data,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }),
                invalidatesTags: ['organizationList'],
            }),

            getOrganizationOrderSets: builder.query<OrderSetResponse[], string>(
                {
                    query: (orgId: string) => ({
                        url: `/v1/ordersets?organizationId=${orgId}`,
                        method: 'GET',
                    }),
                },
            ),

            postOrderSets: builder.mutation<OrderSetResponse, OrderSetRequest>({
                query: (data: OrderSetRequest) => ({
                    url: `/v1/ordersets`,
                    method: 'POST',
                    body: data,
                }),
            }),

            updateOrderSet: builder.mutation<boolean, UpdateOrderSetRequest>({
                query: (data: UpdateOrderSetRequest) => ({
                    url: `/v1/ordersets/${data.id}`,
                    method: 'POST',
                    body: data.data,
                }),
            }),

            deleteOrderSet: builder.mutation<boolean, string>({
                query: (id: string) => ({
                    url: `/v1/ordersets/${id}`,
                    method: 'DELETE',
                }),
            }),

            attachOrderTemplateToOrderSet: builder.mutation<boolean, QueueMedOrderRequest>({
                query: (req: QueueMedOrderRequest ) => ({
                    url: `/v1/ordersets/${req.data.orderSetId}/medication`,
                    method: 'POST',
                    body: req.data,
                }),
            }),

            createOrganization: builder.mutation<any, CreateNewOrganizationRequest>(
                {
                    query: (data:CreateNewOrganizationRequest) => {
                        return {
                            url: `/v1/hospiceorganization/createorganization`,
                            method: 'POST',
                            body: data,
                            headers: {
                                'Content-Type': 'application/json',
                            },
                        };
                    },
                    invalidatesTags: ['organizationList'],
                },
            ),
        }),
    });

export const {
    useGetOrganizationsQuery,
    useExportOrganizationFileMutation,
    useGetOrganizationUsersQuery,
    useGetScriptSureUserMutation,
    useCreateTeamByLocationIdMutation,
    useCreateUserFromScriptSureUserMutation,
    useGetScriptSureUserQQuery,
    useGetUserQuery,
    useToggleUserStatusMutation,
    useCreateLocationMutation,
    useCreatePlaceOfServiceMutation,
    useGetOrganizationOrderSetsQuery,
    usePostOrderSetsMutation,
    useAttachOrderTemplateToOrderSetMutation,
    useCreateOrganizationMutation,
    useUpdateOrderSetMutation,
    useDeleteOrderSetMutation,
} = organizationApi;
